import React, { useState, useRef, useMemo, useEffect } from "react"
import moment from "moment"
import { Spinner } from "reactstrap"

import calendarIcon from "assets/images/icons/calendar.svg"
import { DATE_PERIODS_TYPES } from "constants/DatePeriods"

const MonthSelector = ({
   selectedMonth,
   onChange,
   disabled = false,
   isLoading = false,
   minDate,
   maxDate,
   placeholder = "Select Month",
   productionLineId,
}) => {
   const [isOpen, setIsOpen] = useState(false)
   const wrapperRef = useRef(null)

   const generateMonthOptions = () => {
      const options = []
      if (!minDate) return options

      const startDate = moment(minDate).startOf(DATE_PERIODS_TYPES.MONTH)
      const endDate = maxDate
         ? moment(maxDate).endOf(DATE_PERIODS_TYPES.MONTH)
         : moment().add(1, DATE_PERIODS_TYPES.YEAR)

      let current = startDate.clone()
      while (current.isSameOrBefore(endDate, DATE_PERIODS_TYPES.MONTH)) {
         options.push({
            value: current.toDate(),
            label: current.format("MMMM YYYY"),
            startDate: current.startOf(DATE_PERIODS_TYPES.MONTH).toDate(),
            endDate: current.endOf(DATE_PERIODS_TYPES.MONTH).toDate(),
         })
         current.add(1, DATE_PERIODS_TYPES.MONTH)
      }
      return options
   }

   const options = useMemo(
      () => generateMonthOptions(),
      [minDate, maxDate, productionLineId]
   )

   const handleMonthSelect = option => {
      onChange(option)
      setIsOpen(false)
   }

   const handleInputClick = () => {
      if (!disabled) setIsOpen(!isOpen)
   }

   const handleIconClick = () => {
      if (!disabled) setIsOpen(!isOpen)
   }

   const handleDocumentClick = event => {
      if (
         isOpen &&
         wrapperRef.current &&
         !wrapperRef.current.contains(event.target)
      ) {
         setIsOpen(false)
      }
   }

   useEffect(() => {
      if (isOpen) {
         document.addEventListener("mousedown", handleDocumentClick)
      } else {
         document.removeEventListener("mousedown", handleDocumentClick)
      }
      return () =>
         document.removeEventListener("mousedown", handleDocumentClick)
   }, [isOpen])

   const displayValue = selectedMonth ? selectedMonth.label : placeholder

   return (
      <div className="period-selector-container" ref={wrapperRef}>
         <div
            className={`period-input-wrapper ${
               disabled ? "period-input-disabled" : "period-input-enabled"
            }`}
         >
            <input
               type="text"
               readOnly
               placeholder={isLoading ? "Loading" : placeholder}
               value={isLoading ? "Loading" : displayValue}
               className="period-input-field"
               onClick={handleInputClick}
               disabled={disabled}
            />
            <div className="period-icon-container">
               {isLoading ? (
                  <Spinner className="period-loading-spinner" color="primary" />
               ) : (
                  <img
                     src={calendarIcon}
                     alt="Calendar Icon"
                     className="period-calendar-icon"
                     onClick={handleIconClick}
                  />
               )}
            </div>
         </div>

         {isOpen && (
            <div className="period-dropdown-menu">
               <div className="period-options-list">
                  {options.map((option, index) => (
                     <div
                        key={index}
                        className={`period-option-item ${
                           selectedMonth &&
                           moment(selectedMonth.startDate).isSame(
                              moment(option.startDate),
                              "month"
                           )
                              ? "period-option-selected"
                              : ""
                        }`}
                        onClick={() => handleMonthSelect(option)}
                     >
                        <span className="period-option-label">
                           {option.label}
                        </span>
                        {selectedMonth &&
                           moment(selectedMonth.startDate).isSame(
                              moment(option.startDate),
                              "month"
                           ) && (
                              <span className="period-option-checkmark">✓</span>
                           )}
                     </div>
                  ))}
               </div>
            </div>
         )}
      </div>
   )
}

export default MonthSelector
